import {
  AllergyIntolerance,
  Condition,
  DiagnosticReport,
  Immunization,
  Observation,
  Procedure,
} from "@medplum/fhirtypes";
import { Smart } from "@metriport/fhir-sdk";
import {
  ConditionSeries,
  ImmunizationSeries,
  MedicationSeries,
  ObservationSeries,
} from "../table-transforms/series-utils";

/**
 * SHARED TYPE GUARDS for EHR write-back
 *
 * These type guards are used by both the validator and transformer
 * to detect V2 Series structures and raw FHIR resources.
 */

// ============================================================================
// SERIES TYPE GUARDS (V2 structures)
// ============================================================================

export function isObservationSeries(data: unknown): data is ObservationSeries {
  return (
    typeof data === "object" &&
    data !== null &&
    "resources" in data &&
    "codeDisplay" in data &&
    "code" in data &&
    "resourceType" in data &&
    (data as ObservationSeries).resourceType === "Observation" &&
    Array.isArray((data as ObservationSeries).resources)
  );
}

export function isConditionSeries(data: unknown): data is ConditionSeries {
  return (
    typeof data === "object" &&
    data !== null &&
    "resources" in data &&
    "codeDisplay" in data &&
    "code" in data &&
    "resourceType" in data &&
    (data as ConditionSeries).resourceType === "Condition" &&
    Array.isArray((data as ConditionSeries).resources)
  );
}

export function isImmunizationSeries(data: unknown): data is ImmunizationSeries {
  return (
    typeof data === "object" &&
    data !== null &&
    "resources" in data &&
    "codeDisplay" in data &&
    "code" in data &&
    "resourceType" in data &&
    (data as ImmunizationSeries).resourceType === "Immunization" &&
    Array.isArray((data as ImmunizationSeries).resources)
  );
}

export function isMedicationSeries(data: unknown): data is MedicationSeries {
  return (
    typeof data === "object" &&
    data !== null &&
    "resources" in data &&
    "codeDisplay" in data &&
    "code" in data &&
    "resourceType" in data &&
    (data as MedicationSeries).resourceType === "Medication" &&
    Array.isArray((data as MedicationSeries).resources)
  );
}

// ============================================================================
// RAW FHIR RESOURCE TYPE GUARDS
// ============================================================================

export function isObservation(data: unknown): data is Smart<Observation> {
  return (
    typeof data === "object" &&
    data !== null &&
    "resourceType" in data &&
    (data as Smart<Observation>).resourceType === "Observation"
  );
}

export function isCondition(data: unknown): data is Smart<Condition> {
  return (
    typeof data === "object" &&
    data !== null &&
    "resourceType" in data &&
    (data as Smart<Condition>).resourceType === "Condition"
  );
}

export function isImmunization(data: unknown): data is Smart<Immunization> {
  return (
    typeof data === "object" &&
    data !== null &&
    "resourceType" in data &&
    (data as Smart<Immunization>).resourceType === "Immunization"
  );
}

export function isProcedure(data: unknown): data is Smart<Procedure> {
  return (
    typeof data === "object" &&
    data !== null &&
    "resourceType" in data &&
    (data as Smart<Procedure>).resourceType === "Procedure"
  );
}

export function isAllergyIntolerance(data: unknown): data is Smart<AllergyIntolerance> {
  return (
    typeof data === "object" &&
    data !== null &&
    "resourceType" in data &&
    (data as Smart<AllergyIntolerance>).resourceType === "AllergyIntolerance"
  );
}

export function isDiagnosticReport(data: unknown): data is Smart<DiagnosticReport> {
  return (
    typeof data === "object" &&
    data !== null &&
    "resourceType" in data &&
    (data as Smart<DiagnosticReport>).resourceType === "DiagnosticReport"
  );
}
